import {
  type BrandConfig,
  COLDWELL_BRAND as SHARED_COLDWELL_BRAND,
  FRACTION_BRAND as SHARED_FRACTION_BRAND,
  GRID_BRAND as SHARED_GRID_BRAND,
} from "@fraction/shared";
import { ColdwellLogo, GridLogo, GridLogoDarkBg } from "src/images";
import { cn } from "src/utilities/shadcnUtils";
/**
 * White-label branding, keyed by the hostname the app is served from.
 *
 * We license the dashboard to underwriting desks that run their own branded
 * lender operations. The same deployment is served on multiple domains; the
 * hostname alone decides the brand (no auth/user/application dependency), so
 * branding works on the very first screen (login) and there's never a conflict
 * to disambiguate — whichever domain you're on wins.
 *
 * To add a desk: host its logo (drop an asset under `src/images/partners/` and
 * `import` it, or use an external URL) and add an entry below keyed by its
 * production hostname.
 */
export type Brand = BrandConfig & {
  /** Logo URL (imported asset or hosted URL). Absent → callers render `<Logo/>`. */
  Logo?: string | typeof GridLogo;
  DarkLogo?: string | typeof GridLogoDarkBg;
  faviconHref?: string;
  logoClassName?: string;
};

export const FRACTION_BRAND: Brand = {
  ...SHARED_FRACTION_BRAND,
  logoClassName: "w-[118px] h-5 max-[600px]:w-[94.4px] max-[600px]:h-4",
};
export const GRID_BRAND: Brand = {
  ...SHARED_GRID_BRAND,
  Logo: GridLogo,
  DarkLogo: GridLogoDarkBg,
  faviconHref: "/grid-icon.svg",
  logoClassName: "h-8 w-auto",
};

const DarkColdwellLogo = ({ className, ...props }: any) => (
  <ColdwellLogo {...props} className={cn(className, "text-foreground")} />
);

const LightColdwellLogo = ({ className, ...props }: any) => (
  <ColdwellLogo {...props} className={cn(className, "text-[#012169]")} />
);

export const COLDWELL_BRAND: Brand = {
  ...SHARED_COLDWELL_BRAND,
  Logo: LightColdwellLogo,
  DarkLogo: DarkColdwellLogo,
  faviconHref: "/coldwell-favicon.ico",
  logoClassName: "h-6 w-auto",
};

/** The internal broker workflow is a white-label feature, not a Fraction broker feature. */
export const showsBrokerWorkflowStatuses = (brand: Pick<Brand, "isFraction">): boolean => !brand.isFraction;

const BRANDS_BY_HOST: Record<string, Brand> = {
  "app.fraction.com": FRACTION_BRAND,
  "app.gridapp.ca": GRID_BRAND,
  "coldwell.gridapp.ca": COLDWELL_BRAND,
  localhost: FRACTION_BRAND,
};

/** Resolve the brand for a hostname, defaulting to Fraction. */
export const resolveBrand = (hostname: string): Brand => {
  return BRANDS_BY_HOST[hostname.replace(/^www\./, "").toLowerCase()] ?? FRACTION_BRAND;
};
